import React, { useState, useEffect } from "react";

import { Link, useNavigate } from "react-router-dom";

import toastManager from "../../helpers/toastManager";

// API
import useAxios from "../../hooks/useAxios";
import jsonFormatAuth from "../../apis/jsonFormatAuth";

// Keeping track of menu expansion
import { useSelector, useDispatch } from "react-redux";
import {
  // setMenuOpen,
  setActivePage,
  selectActivePage,
  // setActiveMenu,
  // selectActiveMenu,
} from "../../redux/features/applicationState/applicationStateSlice";

import { resetPatient } from "../../redux/features/patientState/patientStateSlice";

// Icons
import { ChevronDownIcon } from "@heroicons/react/24/outline";

const SidebarShortcutSmall = ({ shortcutText, Icon, pageLink }) => {
  const dispatch = useDispatch();
  const activePage = useSelector(selectActivePage);

  const handleActivePageUpdate = () => {
    dispatch(setActivePage(pageLink));
  };

  return (
    <div className="py-2">
      <Link to={{ pathname: pageLink }} onClick={handleActivePageUpdate}>
        <div
          className={`no-underline flex flex-row items-center justify-center h-20 w-full py-2 rounded-xl cursor-pointer
                      hover:bg-opacity-80
                ${
                  activePage === pageLink
                    ? "outline outline-1 bg-lime-200"
                    : "bg-lime-100"
                }
            `}
        >
          <div className="flex flex-[3] items-center justify-start h-full w-full pl-2 Xbg-pink-500">
            <p className="text-lg md:text-xl font-medium text-neutral-800">
              {shortcutText}
            </p>
          </div>
          <div className="flex flex-[1] items-center justify-end pr-2 h-full w-full Xbg-blue-500">
            <Icon className="text-black w-7 h-7 Xbg-blue-500" />
          </div>
        </div>
      </Link>
    </div>
  );
};

const SidebarShortcutMedium = ({ shortcutText, Icon, pageLink }) => {
  const dispatch = useDispatch();
  const activePage = useSelector(selectActivePage);

  const handleActivePageUpdate = () => {
    dispatch(setActivePage(pageLink));
  };

  return (
    <div className="py-2">
      <Link to={{ pathname: pageLink }} onClick={handleActivePageUpdate}>
        <div
          className={`flex flex-row items-center justify-center h-40 w-full py-2 rounded-xl cursor-pointer
                      hover:bg-opacity-80
                ${
                  activePage === pageLink
                    ? "outline outline-1 bg-emerald-200"
                    : "bg-emerald-100"
                }
              `}
        >
          <div className="flex flex-[3] items-end justify-start h-full w-full pl-2 Xbg-pink-500">
            <p className="text-3xl md:text-4xl font-medium text-neutral-800">
              {shortcutText}
            </p>
          </div>
          <div className="flex flex-[1] items-end justify-end pr-2 h-full w-full Xbg-blue-500">
            <Icon className="text-black w-12 h-12 Xbg-blue-500" />
          </div>
        </div>
      </Link>
    </div>
  );
};

const SidebarShortcutLarge = ({ shortcutText, Icon, pageLink }) => {
  const dispatch = useDispatch();
  const activePage = useSelector(selectActivePage);

  const handleActivePageUpdate = () => {
    dispatch(setActivePage(pageLink));
  };

  return (
    <div className="py-2">
      <Link to={{ pathname: pageLink }} onClick={handleActivePageUpdate}>
        <div
          className={`flex flex-col items-center justify-center h-96 w-full py-2 rounded-xl cursor-pointer
                      hover:bg-opacity-80
                  ${
                    activePage === pageLink
                      ? "outline outline-1 bg-blue-200"
                      : "bg-blue-100"
                  }
                `}
        >
          <div className="flex flex-[2] items-center justify-center h-full w-full pt-4 Xbg-blue-500">
            <Icon className="text-black w-44 h-44 Xbg-blue-500 stroke-[0.5]" />
          </div>
          <div className="flex flex-[1] items-center justify-center h-full w-full Xbg-pink-500">
            <p className="text-4xl font-medium text-neutral-800 text-center">
              {shortcutText}
            </p>
          </div>
        </div>
      </Link>
    </div>
  );
};

const SidebarShortcutMediumRapidEntry = ({
  shortcutText,
  Icon,
  apiPath,
  newItemPath,
}) => {
  const { response, axiosFetch } = useAxios();

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const activePage = useSelector(selectActivePage);

  const [pageLink, setPageLink] = useState("");

  const activePageUpdateHandler = (currentPageLink) => {
    dispatch(setActivePage(currentPageLink));
    // Send a request to the backend
  };

  const handleCreateNewEntry = () => {
    axiosFetch({
      axiosInstance: jsonFormatAuth(),
      method: "POST",
      url: `${apiPath}`,
      sendSignal: false,
      requestConfig: { generate_rapid_patient: true },
    });
  };

  useEffect(() => {
    // console.log(response);

    if (response?.action_code === "rapid_entry_created") {
      const entryId = response?.payload?.entry_id;
      const rapidPatientId = response?.payload?.rapid_patient_id;

      if (entryId && rapidPatientId) {
        const currentPageLink = `${newItemPath}${entryId}/`;

        // Clear current selected patient
        dispatch(resetPatient());

        setPageLink(currentPageLink);
        activePageUpdateHandler(currentPageLink);
        navigate(currentPageLink);
        toastManager(
          "success",
          "Rapid Entry Created!",
          "Entry has been created successfully."
        );
      }
    }
  }, [response]);

  return (
    <div className="py-2">
      <div
        className={`flex flex-row items-center justify-center h-40 w-full py-2 rounded-xl cursor-pointer
                    hover:bg-opacity-80
                ${
                  activePage === pageLink
                    ? "outline outline-1 bg-emerald-200"
                    : "bg-emerald-100"
                }
              `}
        onClick={handleCreateNewEntry}
      >
        <div className="flex flex-[3] items-end justify-start h-full w-full pl-2 Xbg-pink-500">
          <p className="text-3xl md:text-4xl font-medium text-neutral-800">
            {shortcutText}
          </p>
        </div>
        <div className="flex flex-[1] items-end justify-end pr-2 h-full w-full Xbg-blue-500">
          <Icon className="text-black w-12 h-12 Xbg-blue-500" />
        </div>
      </div>
    </div>
  );
};

const ShortcutsGroup = ({
  groupText,
  groupHeight,
  children,
  groupName,
  activeGroup,
  setActiveGroup,
}) => {
  const [groupOpen, setGroupOpen] = useState(false);

  // Always open group if it becomes active
  useEffect(() => {
    if (activeGroup === groupName) {
      setGroupOpen(true);
    }
  }, [activeGroup]);

  return (
    <div className="py-2">
      <div
        className="flex flex-col rounded-xl 
                 bg-neutral-50
                   hover:bg-opacity-80
                   border border-1 border-neutral-300
                  "
      >
        {/* Group header */}
        <div
          className="flex flex-row h-12 p-2 cursor-pointer Xbg-red-300"
          onClick={() => {
            // Allow user to collapse / expand group
            setGroupOpen(!groupOpen);
            // Update active group
            setActiveGroup();
          }}
        >
          <div className="flex flex-[3] flex-row items-center justify-start">
            <p className="text-lg md:text-xl font-medium text-neutral-900">
              {groupText}
            </p>
          </div>
          <div className="flex flex-[1] items-center justify-end h-full w-full Xbg-blue-500">
            <ChevronDownIcon
              className={`text-black w-7 h-7  flex-none transition-all duration-200 ${
                activeGroup === groupName && groupOpen && "rotate-180"
              }`}
              aria-hidden="true"
            />
          </div>
        </div>

        {/* Small: 96px tall */}
        {/* Medium: 192px tall */}
        {/* Large: 416px tall */}

        {/* 4px is added to account for some padding at bottom. */}

        <div
          className={`flex flex-col px-2 overflow-hidden Xbg-red-500
           transition-all duration-100 
           ${!(activeGroup === groupName && groupOpen) ? "" : "pb-1"}
           `}
          style={
            !(activeGroup === groupName && groupOpen)
              ? { height: 0 }
              : { height: groupHeight + 4 }
          }
        >
          {children}
        </div>
      </div>
    </div>
  );
};

export {
  SidebarShortcutSmall,
  SidebarShortcutMedium,
  SidebarShortcutLarge,
  SidebarShortcutMediumRapidEntry,
  ShortcutsGroup,
};
